import { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, CheckCircle2, Database, PlayCircle, Eye } from "lucide-react";

interface PreviewData {
  total: number;
  reclassifications: {
    toOperator: number;
    toConsolidator: number;
    stayRestaurant: number;
  };
  fieldsToClean: Record<string, number>;
}

interface ExecutionResult {
  processed: number;
  reclassified: {
    toOperator: number;
    toConsolidator: number;
    toRestaurant: number;
  };
  cleaned: {
    city: number;
    state: number;
    region: number;
    phone: number;
    cellPhone: number;
    other: number;
  };
}

export default function ContactMigration() {
  const { toast } = useToast();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setPreviewData(null);
    setExecutionResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('migrate-contacts', {
        body: { mode: 'preview' },
      });

      if (error) throw error;

      setPreviewData(data);
      toast({
        title: "Análise concluída",
        description: `${data.total} contatos analisados`,
      });
    } catch (error: any) {
      console.error('Error analyzing contacts:', error);
      toast({
        title: "Erro na análise",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleMigrate = async () => {
    setIsMigrating(true);
    setProgress(0);
    setExecutionResult(null);

    try {
      // Simular progresso durante a migração
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const { data, error } = await supabase.functions.invoke('migrate-contacts', {
        body: { mode: 'execute' },
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (error) throw error;

      setExecutionResult(data);
      toast({
        title: "Migração concluída com sucesso",
        description: `${data.processed} contatos processados`,
      });
    } catch (error: any) {
      console.error('Error migrating contacts:', error);
      toast({
        title: "Erro na migração",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1 p-8">
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Migração de Contatos</h1>
              <p className="text-muted-foreground mt-2">
                Normalize e classifique automaticamente os contatos existentes
              </p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Importante</AlertTitle>
              <AlertDescription>
                Esta operação irá modificar os dados dos contatos no banco de dados. 
                Recomendamos fazer um backup antes de prosseguir.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Análise de Dados
                </CardTitle>
                <CardDescription>
                  Primeiro, analise os dados para ver o que será modificado
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing || isMigrating}
                  className="w-full"
                  variant="outline"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {isAnalyzing ? "Analisando..." : "Analisar Dados"}
                </Button>

                {previewData && (
                  <div className="space-y-4 p-4 bg-muted rounded-lg">
                    <h3 className="font-semibold">Resumo da Análise</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-background rounded-lg">
                        <p className="text-sm text-muted-foreground">Total de Contatos</p>
                        <p className="text-2xl font-bold">{previewData.total}</p>
                      </div>
                      
                      <div className="p-3 bg-background rounded-lg">
                        <p className="text-sm text-muted-foreground">Reclassificações</p>
                        <p className="text-2xl font-bold">
                          {previewData.reclassifications.toOperator + 
                           previewData.reclassifications.toConsolidator}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Mudanças de Tipo:</h4>
                      <ul className="space-y-1 text-sm">
                        <li className="flex justify-between">
                          <span className="text-muted-foreground">→ Operadoras:</span>
                          <span className="font-semibold text-green-600">
                            {previewData.reclassifications.toOperator}
                          </span>
                        </li>
                        <li className="flex justify-between">
                          <span className="text-muted-foreground">→ Consolidadoras:</span>
                          <span className="font-semibold text-purple-600">
                            {previewData.reclassifications.toConsolidator}
                          </span>
                        </li>
                        <li className="flex justify-between">
                          <span className="text-muted-foreground">Permanecem Restaurantes:</span>
                          <span className="font-semibold text-blue-600">
                            {previewData.reclassifications.stayRestaurant}
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Campos a Limpar:</h4>
                      <ul className="space-y-1 text-sm">
                        {Object.entries(previewData.fieldsToClean).map(([field, count]) => (
                          <li key={field} className="flex justify-between">
                            <span className="text-muted-foreground capitalize">{field}:</span>
                            <span className="font-semibold">{count}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PlayCircle className="h-5 w-5" />
                  Executar Migração
                </CardTitle>
                <CardDescription>
                  Execute a migração para aplicar as mudanças no banco de dados
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleMigrate}
                  disabled={!previewData || isAnalyzing || isMigrating}
                  className="w-full"
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  {isMigrating ? "Migrando..." : "Executar Migração"}
                </Button>

                {isMigrating && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progresso:</span>
                      <span className="font-semibold">{progress}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                )}

                {executionResult && (
                  <Alert className="border-green-600 bg-green-50 dark:bg-green-950">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800 dark:text-green-200">
                      Migração Concluída!
                    </AlertTitle>
                    <AlertDescription className="text-green-700 dark:text-green-300">
                      <div className="space-y-2 mt-2">
                        <p><strong>{executionResult.processed}</strong> contatos processados</p>
                        
                        <div className="space-y-1 text-sm">
                          <p className="font-semibold">Reclassificações:</p>
                          <ul className="ml-4 space-y-1">
                            <li>→ {executionResult.reclassified.toOperator} para Operadora</li>
                            <li>→ {executionResult.reclassified.toConsolidator} para Consolidadora</li>
                          </ul>
                        </div>

                        <div className="space-y-1 text-sm">
                          <p className="font-semibold">Campos Limpos:</p>
                          <ul className="ml-4 space-y-1">
                            <li>Cidade: {executionResult.cleaned.city}</li>
                            <li>Estado: {executionResult.cleaned.state}</li>
                            <li>Região: {executionResult.cleaned.region}</li>
                            <li>Telefone: {executionResult.cleaned.phone}</li>
                            <li>Celular: {executionResult.cleaned.cellPhone}</li>
                            <li>Outros: {executionResult.cleaned.other}</li>
                          </ul>
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
